import { computed, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { patchState, signalStore, withComputed, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { injectDispatch, withEventHandlers, Events } from '@ngrx/signals/events';
import { withResourcePolling } from '@polling';
import { messagingPageEvents } from '@messaging/events';
import { visitBridgePageEvents } from '@visit-bridge';
import { EMPTY, switchMap, tap, throttleTime } from 'rxjs';
import {
  Message,
  MessageFilter,
  MessageThread,
  MessageThreadResponse,
  MessagingStoreState,
  DEFAULT_OFFSET,
  initialState,
} from '../../../../util/src/index';
import {
  MessageCategoriesEndpoint,
  MessageEndpoint,
  MessageLockEndpoint,
  PatientMessagesEndpoint,
  MessagesEndpoint,
} from '../services/messaging.endpoints.service';

const POLL_INTERVAL = 5000;

export const MessagingStore = signalStore(
  withState<MessagingStoreState>(initialState),
  withResourcePolling({ intervalMs: POLL_INTERVAL, pollingName: 'messaging-thread' }),
  withProps(() => ({
    messagesEndpoint: new MessagesEndpoint(),
    messageEndpoint: new MessageEndpoint(),
    patientMessagesEndpoint: new PatientMessagesEndpoint(),
    categoriesEndpoint: new MessageCategoriesEndpoint(),
    lockEndpoint: new MessageLockEndpoint(),
    dispatch: injectDispatch(messagingPageEvents),
    loadTrigger: signal<Record<string, string> | undefined>(undefined),
    patientMessagesTrigger: signal<{ patientId: number; params: Record<string, string> } | undefined>(undefined),
  })),
  withEventHandlers((store, events = inject(Events)) => ({
    onActivePatientLoaded$: events.on(visitBridgePageEvents.activePatientLoaded).pipe(
      tap(({ payload }) => {
        patchState(store, { activePatientId: payload.patientId });
      }),
    ),
  })),
  withProps(store => {
    const categoriesResource = rxResource({ stream: () => store.categoriesEndpoint.get() });

    const messagesResource = rxResource({
      params: () => store.loadTrigger(),
      stream: ({ params }) => (params ? store.messagesEndpoint.get(params) : EMPTY),
    });

    const patientMessagesResource = rxResource({
      params: () => store.patientMessagesTrigger(),
      stream: ({ params }) => (params ? store.patientMessagesEndpoint.get(params.patientId, params.params) : EMPTY),
      equal: (previous, current) => {
        const { data: prevData, metadata: prevMeta } = previous || {};
        const { data: curData, metadata: curMeta } = current || {};
        if (!prevData || !curData) return prevData === curData;
        if (prevData.length !== curData.length) return false;
        return prevMeta?.num_unread === curMeta?.num_unread;
      },
    });

    return {
      categoriesResource: categoriesResource.asReadonly(),
      messagesResource: messagesResource.asReadonly(),
      patientMessagesResource: patientMessagesResource.asReadonly(),
      _patientMessagesResourceRef: patientMessagesResource,
    };
  }),
  withComputed(store => ({
    // Derived from resources — pure, no side effects
    messages: computed<Message[]>(() => store.messagesResource.value()?.data ?? []),
    metadata: computed(() => store.messagesResource.value()?.metadata ?? null),
    categories: computed(() => store.categoriesResource.value() ?? []),
    patientMessages: computed<Message[]>(() => store.patientMessagesResource.value()?.data ?? []),
    patientMessagesMetadata: computed(() => store.patientMessagesResource.value()?.metadata ?? null),

    // Derived from patientMessages
    patientMessageThreads: computed<MessageThread[]>(() => {
      const grouped = new Map<number, Message[]>();
      for (const msg of store.patientMessagesResource.value()?.data ?? []) {
        const id = msg.from.id;
        const existing = grouped.get(id) ?? [];
        grouped.set(id, [...existing, msg]);
      }
      return Array.from(grouped.entries()).map(([patientId, messages]) => ({
        patientId,
        lastMessage: messages[0],
        count: messages.length,
      }));
    }),

    unreadCount: computed(() => store.messagesResource.value()?.metadata?.num_unread ?? 0),

    hasMore: computed(() => {
      const data = store.messagesResource.value();
      return data ? data.data.length < data.metadata.total : false;
    }),

    isLoading: computed(() => store.messagesResource.isLoading() || store.patientMessagesResource.isLoading()),

    patientSummaryData: computed(() => {
      const patientId = store.activePatientId();
      const firstMessage = store.patientMessagesResource.value()?.data[0];
      if (firstMessage && patientId === firstMessage.from.id) {
        return firstMessage.from;
      } else if (firstMessage && patientId === firstMessage.to.id) {
        return firstMessage.to;
      }
      return null;
    }),
  })),
  withMethods(store => {
    const buildParams = (overrides: Partial<MessagingStoreState['pagination']> = {}): Record<string, string> => {
      const p = { ...store.pagination(), ...overrides };
      const params: Record<string, string> = {
        limit: String(p.limit),
        offset: String(p.offset),
        order: p.order,
        read: p.read,
      };
      if (p.category_id > 0) params['category_id'] = String(p.category_id);
      if (p.state.length) params['state'] = p.state.map(s => s.code).join('|');
      return params;
    };

    const loadMessages = rxMethod<void>(trigger$ =>
      trigger$.pipe(
        throttleTime(300),
        tap(() => store.loadTrigger.set(buildParams())),
      ),
    );

    const loadMore = rxMethod<void>(trigger$ =>
      trigger$.pipe(
        throttleTime(300),
        tap(() => {
          const nextOffset = store.pagination().offset + store.pagination().limit;
          store.loadTrigger.set(buildParams({ offset: nextOffset }));
        }),
      ),
    );

    const openPatientMessages = rxMethod<number>(trigger$ =>
      trigger$.pipe(
        throttleTime(300),
        tap(patientId => {
          patchState(store, { activePatientId: patientId });
          store.patientMessagesTrigger.set({ patientId, params: {} });
        }),
      ),
    );

    const startPolling = () => {
      store.startResourcePolling({
        resources: [
          {
            reload: {
              reload: () => {
                store._patientMessagesResourceRef.reload();
              },
            },
            condition: computed(() => !!store.activePatientId()),
          },
        ],
      });
    };

    const applyFilter = rxMethod<Partial<MessageFilter>>(filter$ =>
      filter$.pipe(
        throttleTime(300),
        tap(filterPatch => {
          const current = store.filter();
          const updated = { ...current, ...filterPatch };
          patchState(store, {
            filter: updated,
            pagination: {
              ...store.pagination(),
              offset: DEFAULT_OFFSET,
              category_id: updated.categoryId,
              read: updated.read,
              order: updated.order,
              state: updated.states,
            },
          });
          store.loadTrigger.set(buildParams({ offset: DEFAULT_OFFSET }));
        }),
      ),
    );

    const markPatientMessagesAsRead = rxMethod<{ patientId: number; messageId?: number }>(trigger$ =>
      trigger$.pipe(
        throttleTime(300),
        switchMap(({ patientId, messageId }) =>
          store.patientMessagesEndpoint
            .put({ user_id: patientId, message_id: messageId })
            .pipe(tap(() => store.loadTrigger.set(buildParams()))),
        ),
      ),
    );

    const sendMessage = rxMethod<{
      patientId: number;
      content: string;
      entryType: 'message' | 'sms';
      mobileNo?: string;
    }>(trigger$ =>
      trigger$.pipe(
        throttleTime(300),
        switchMap(({ patientId, content, entryType, mobileNo }) =>
          store.messagesEndpoint
            .post({
              to_id: patientId,
              content,
              entry_type: entryType,
              ...(mobileNo ? { mobile_no: mobileNo } : {}),
            })
            .pipe(
              tap(response => {
                store.patientMessagesTrigger.set({ patientId, params: {} });
                store.dispatch.messageSent({ patientId, messageId: response.id });
              }),
            ),
        ),
      ),
    );

    const updateCategory = rxMethod<{ messageId: number; categoryId: number }>(trigger$ =>
      trigger$.pipe(
        throttleTime(300),
        switchMap(({ messageId, categoryId }) =>
          store.messageEndpoint
            .put(messageId, { category_id: categoryId })
            .pipe(tap(() => store.loadTrigger.set(buildParams()))),
        ),
      ),
    );

    const updateReadStatus = rxMethod<{ messageId: number; readAt: string | null }>(trigger$ =>
      trigger$.pipe(
        throttleTime(300),
        switchMap(({ messageId, readAt }) =>
          store.messageEndpoint
            .put(messageId, { read_at: readAt })
            .pipe(tap(() => store.loadTrigger.set(buildParams()))),
        ),
      ),
    );

    const checkLock = rxMethod<number>(patientId$ =>
      patientId$.pipe(
        throttleTime(300),
        switchMap(patientId =>
          store.lockEndpoint
            .get(patientId)
            .pipe(tap(lock => patchState(store, { lockPatientId: lock.clinician ? patientId : null }))),
        ),
      ),
    );

    const acquireLock = rxMethod<number>(patientId$ =>
      patientId$.pipe(
        throttleTime(300),
        switchMap(patientId => store.lockEndpoint.post(patientId)),
      ),
    );

    const goToPage = (page: number) => {
      const offset = (page - 1) * store.pagination().limit;
      store.loadTrigger.set(buildParams({ offset }));
    };

    return {
      loadMessages,
      loadMore,
      openPatientMessages,
      startPolling,
      applyFilter,
      markPatientMessagesAsRead,
      sendMessage,
      updateCategory,
      updateReadStatus,
      checkLock,
      acquireLock,
      goToPage,
    };
  }),
);

export type MessagingStore = InstanceType<typeof MessagingStore>;
